import {Formik, FormikHelpers, FormikProps} from 'formik';
import {HTMLReactParserOptions, Element as ParserElement, DOMNode, domToReact} from 'html-react-parser';
import {isEmpty} from 'lodash';
import {useCallback, useState, useMemo, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {
    Button,
    InputFormik,
    PhoneInputFormik,
    TextAreaFormik,
    Icon,
    FormGroup,
    NodeElement,
    NotRobotCaptcha,
    ClientDynamicContent,
    Loader,
    notifier
} from 'components';
import {RequestAppointmentUnits as Units, REQUEST_APPOINTMENT_DEFAULT_TEMPLATE, DynamicComponentNames} from 'constants/client';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {ApiError} from 'types';
import {logger} from 'utils';
import {dynamicContentUtils} from 'utils/dynamic-content';

import {RequestAppointmentSubmitted} from './RequestAppointmentSubmitted';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.REQUEST_APPOINTMENT_VIEW;

const FIELDS = {
    NAME: 'name',
    PHONE: 'phone',
    PHONE_TEXT: 'phone-text',
    EMAIL: 'email',
    MESSAGE: 'message',
    CAPTCHA: 'captcha',
    CAPTCHA_TOKEN: 'captchaToken'
};

const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        title, description, handleCancel, handleSubmit, submitDisabled, localParticipant,
        renderForm, renderNotStyledInput, hasCancelButton, renderCancel, renderSubmit,
    } = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.Icon, node)) {
                return (
                    <NodeElement node={node}>
                        <Icon name='agent-unavailable' size='6xl' color='default-dimmed' />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Title, node)) {
                return <NodeElement node={node}>{title}</NodeElement>;
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.FormDefault, node)) {
                return <NodeElement node={node}>{renderForm()}</NodeElement>;
            }
            if (isMatchElement(Units.NameInputContainer, node)) {
                return renderNotStyledInput(node, FIELDS.NAME);
            }
            if (isMatchElement(Units.PhoneInputContainer, node)) {
                return renderNotStyledInput(node, FIELDS.PHONE);
            }
            if (isMatchElement(Units.PhoneTextInputContainer, node)) {
                return renderNotStyledInput(node, FIELDS.PHONE_TEXT);
            }
            if (isMatchElement(Units.EmailInputContainer, node)) {
                return renderNotStyledInput(node, FIELDS.EMAIL);
            }
            if (isMatchElement(Units.MessageInputContainer, node)) {
                return renderNotStyledInput(node, FIELDS.MESSAGE);
            }
            if (isMatchElement(Units.CaptchaContainer, node)) {
                return renderNotStyledInput(node, FIELDS.CAPTCHA);
            }
            if (isMatchElement(Units.CancelButton, node)) {
                return (
                    <NodeElement show={hasCancelButton} node={node} onClick={handleCancel}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.CancelButtonDefault, node)) {
                return <NodeElement show={hasCancelButton} node={node}>{renderCancel()}</NodeElement>;
            }
            if (isMatchElement(Units.SubmitButton, node)) {
                return (
                    <NodeElement node={node} disabled={submitDisabled} onClick={handleSubmit}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.SubmitButtonDefault, node)) {
                return <NodeElement node={node}>{renderSubmit()}</NodeElement>;
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

type FormFields = {
    name: string;
    phone: string;
    email: string;
    message: string;
    captchaToken: string;
};

type FormTemplateProps = {
    isLoading: boolean;
    isSubmitted: boolean;
    setSubmitted: (value: boolean) => void;
    closeForm?: () => void;
} & FormikProps<FormFields>;

const initialValues: FormFields = {
    name: '',
    phone: '',
    email: '',
    message: '',
    captchaToken: ''
};

const FormTemplate = ({
    isLoading,
    isSubmitted,
    submitForm,
    resetForm,
    closeForm,
    setFieldValue,
    setSubmitted,
    values,
    errors
}: FormTemplateProps) => {
    const {t} = useTranslation();
    const {image} = useClientSession();
    const disabled = useMemo(() => !values.captchaToken || isLoading || !isEmpty(errors)
        || !values.name || !values.email || !values.phone || !values.message, [isLoading, values, errors]);

    const hasCancelButton = useMemo(() => !!closeForm, [closeForm]);
    const handleCancel = useCallback(() => {
        resetForm();
        closeForm();
        setSubmitted(false);
    }, [closeForm, resetForm, setSubmitted]);

    const handleSubmit = useCallback(() => {
        if (!disabled) {
            submitForm();
        }
    }, [disabled, submitForm]);

    const onCaptchaChange = useCallback((token: string) => {
        setFieldValue(FIELDS.CAPTCHA_TOKEN, token);
    }, [setFieldValue]);

    const onCaptchaError = useCallback(() => {
        setFieldValue(FIELDS.CAPTCHA_TOKEN, null);
    }, [setFieldValue]);

    const renderInput = useCallback((name: string, styled = true) => {
        if (name === FIELDS.NAME) {
            return (
                <InputFormik
                    name={FIELDS.NAME}
                    required
                    styled={styled}
                    label={t(T_KEYS.FIELDS.NAME)}
                />
            );
        } else if (name === FIELDS.PHONE) {
            return (
                <PhoneInputFormik
                    name={FIELDS.PHONE}
                    styled={styled}
                    required
                    label={t(T_KEYS.FIELDS.PHONE)}
                />);
        } else if (name === FIELDS.PHONE_TEXT) {
            return (
                <InputFormik
                    name='phone'
                    required
                    styled={styled}
                    label={t(T_KEYS.FIELDS.PHONE)}
                />);
        } else if (name === FIELDS.EMAIL) {
            return (
                <InputFormik
                    name={FIELDS.EMAIL}
                    required
                    styled={styled}
                    label={t(T_KEYS.FIELDS.EMAIL)}
                />);
        } else if (name === FIELDS.MESSAGE) {
            return (
                <TextAreaFormik
                    name={FIELDS.MESSAGE}
                    required
                    rows={3}
                    styled={styled}
                    label={t(T_KEYS.FIELDS.MESSAGE)}
                />
            );
        } else if (name === FIELDS.CAPTCHA) {
            return (
                <NotRobotCaptcha
                    onChange={onCaptchaChange}
                    onErrored={onCaptchaError}
                    onExpired={onCaptchaError}
                />
            );
        }
    }, [t, onCaptchaChange, onCaptchaError]);

    const renderNotStyledInput = useCallback((node: ParserElement, name: string) =>
        <NodeElement node={node}>{renderInput(name, false)}</NodeElement>, [renderInput]);

    const renderCancel = useCallback(() => hasCancelButton ? (
        <Button width='full' height='full' onClick={handleCancel}>
            {t(T_KEYS.ACTIONS.CANCEL)}
        </Button>
    ) : null, [t, handleCancel, hasCancelButton]);

    const renderSubmit = useCallback(() => (
        <Button
            color='primary'
            width='full'
            height='full'
            className='submit'
            disabled={disabled}
            onClick={handleSubmit}
        >
            {t(T_KEYS.ACTIONS.SUBMIT)}
        </Button>
    ), [t, disabled, handleSubmit]);

    const renderForm = useCallback(() => (
        <form className='controls'>
            <FormGroup>{renderInput(FIELDS.NAME)}</FormGroup>
            <FormGroup>{renderInput(FIELDS.PHONE)}</FormGroup>
            <FormGroup>{renderInput(FIELDS.EMAIL)}</FormGroup>
            <FormGroup>{renderInput(FIELDS.MESSAGE)}</FormGroup>
            <FormGroup>{renderInput(FIELDS.CAPTCHA)}</FormGroup>
            <div className='actions'>
                {renderCancel()}
                {renderSubmit()}
            </div>
            {isLoading && <div className='loader'><Loader /></div>}
        </form>
    ), [isLoading, renderInput, renderCancel, renderSubmit]);

    if (isSubmitted) {
        return <RequestAppointmentSubmitted />;
    }

    return (
        <ClientDynamicLayout image={image}>
            <ClientDynamicContent
                component={DynamicComponentNames.RequestAppointment}
                defaultTemplate={REQUEST_APPOINTMENT_DEFAULT_TEMPLATE}
                optionsProps={{
                    title: t(T_KEYS.HEADER),
                    description: t(T_KEYS.DETAILS),
                    submitDisabled: disabled,
                    renderNotStyledInput,
                    renderForm,
                    hasCancelButton,
                    renderCancel,
                    renderSubmit,
                    handleCancel,
                    handleSubmit,
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};

type Props = {
    onCloseForm?: () => void;
};

export const RequestAppointmentView = ({onCloseForm}: Props) => {
    const {t} = useTranslation();
    const {requestAppointment} = useContext(ClientContext);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isSubmitted, setSubmitted] = useState<boolean>(false);

    const validationSchema = useMemo(() => Yup.object().shape({
        name: Yup.string().required(t(T_KEYS.ERRORS.EMPTY)),
        phone: Yup.string().required(t(T_KEYS.ERRORS.EMPTY)),
        email: Yup.string().email(t(T_KEYS.ERRORS.INCORRECT_FORMAT)).required(t(T_KEYS.ERRORS.EMPTY)),
        message: Yup.string().required(t(T_KEYS.ERRORS.EMPTY)).max(200)
    }), [t]);

    const submit = useCallback(async (values: FormFields, formikHelpers: FormikHelpers<FormFields>) => {
        try {
            setLoading(true);
            await requestAppointment(values);
            setSubmitted(true);
            notifier.success({details: t(T_KEYS.RESULTS.MESSAGE_SENT)});
        } catch (e) {
            const error = e as ApiError;
            if (error?.response.data === FIELDS.EMAIL) {
                formikHelpers.setFieldError(FIELDS.EMAIL, t(T_KEYS.ERRORS.INCORRECT_FORMAT));
            }
            notifier.error({details: t(T_KEYS.RESULTS.MESSAGE_SENDING_FAILED)});
            logger.error(error);
        } finally {
            setLoading(false);
        }
    }, [t, requestAppointment]);

    return (
        <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={submit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    isLoading={isLoading}
                    isSubmitted={isSubmitted}
                    setSubmitted={setSubmitted}
                    closeForm={onCloseForm}
                />
            )}
        </Formik>
    );
};
